import { BLK_API_PATHS, CHAT_BLK } from '../constants';
import {
    ChatblkFeedbackAnswerQualityRating,
    ChatBLKFeedbackApiRequest,
    ChatBLKFeedbackEvent,
    ChatblkFeedbackNotificationType,
    ChatblkFeedbackQuestionCategory,
    ChatblkFeedbackRelatedQuestionsQualityRating,
    ChatblkLaunchMode,
    ChatblkMfeFunction,
    ChatblkMfeFunctionMap,
    ChatBlkQuery,
    ChatBLKUiEventApiRequest,
    GenericInterface,
    telemetryFunctionMap,
} from '../types';
import { uuidv4 } from '../utils';
import { ChatBlkBaseService } from './ChatBlkBaseService';

const chatBlk = CHAT_BLK.toLowerCase();

export const ChatBlkTelemetryServiceFunction = {
    FEEDBACK: `${chatBlk}:feedback`,
    UI: `${chatBlk}:ui`,
};

export class ChatBlkTelemetryService extends ChatBlkBaseService {
    protected static instance: ChatBlkTelemetryService;
    protected static actionOrder = 0;

    public static getInstance(): ChatBlkTelemetryService {
        if (
            ChatBlkBaseService.getInstance().appContext &&
            !ChatBlkTelemetryService.instance
        ) {
            ChatBlkTelemetryService.instance = new ChatBlkTelemetryService();
            ChatBlkTelemetryService.track(
                telemetryFunctionMap.CHATBLK_MFE_FUNCTION_LAUNCH
            );
        }

        return ChatBlkTelemetryService.instance;
    }

    private static getKey(object, index): string {
        return Object.keys(object).find((key) => object[key] === index);
    }

    private static convertOptions(options = {}): GenericInterface<string> {
        return Object.entries(options).reduce((acc, [key, value]) => {
            acc[key] = `${value}`;
            return acc;
        }, {});
    }

    public static trackFeedback(query: ChatBlkQuery): void {
        const event = ChatBlkTelemetryService.baseUiEvent();
        const feedbackEvent =
            ChatBlkTelemetryService.createFeedbackEvent(query);

        event.chatblkFunction = ChatBlkTelemetryService.getKey(
            ChatblkMfeFunction,
            telemetryFunctionMap.CHATBLK_MFE_FUNCTION_CHAT_BUBBLE_FEEDBACK_SUBMITTED
        );
        event.chatblkId = feedbackEvent.chatblkExchangeId;

        const uiEventApiRequest: ChatBLKUiEventApiRequest = {
            id: uuidv4(),
            chatblkUiEvent: event,
        };

        const feedbackEventApiRequest: ChatBLKFeedbackApiRequest = {
            id: uuidv4(),
            chatblkFeedbackEvent: feedbackEvent,
        };

        ChatBlkTelemetryService.instance.postUIEvent(uiEventApiRequest);
        ChatBlkTelemetryService.instance.postFeedbackEvent(
            feedbackEventApiRequest
        );
    }

    public static track(
        chatBlkFunction: ChatblkMfeFunctionMap[keyof ChatblkMfeFunctionMap],
        options?: unknown,
        queryResponseId?: string
    ): void {
        const event = ChatBlkTelemetryService.baseUiEvent();
        event.chatblkFunction = ChatBlkTelemetryService.getKey(
            ChatblkMfeFunction,
            chatBlkFunction
        );
        if (queryResponseId) {
            event.chatblkId = queryResponseId;
        }
        if (options) {
            event.options = ChatBlkTelemetryService.convertOptions(options);
        }

        const uiEventApiRequest: ChatBLKUiEventApiRequest = {
            id: uuidv4(),
            chatblkUiEvent: event,
        };

        ChatBlkTelemetryService.instance.postUIEvent(uiEventApiRequest);
    }

    public static trackError(options: unknown): void {
        const event = ChatBlkTelemetryService.baseUiEvent();
        event.chatblkFunction = ChatBlkTelemetryService.getKey(
            ChatblkMfeFunction,
            telemetryFunctionMap.CHATBLK_MFE_FUNCTION_ERROR
        );
        event.options = ChatBlkTelemetryService.convertOptions(options);

        const uiEventApiRequest: ChatBLKUiEventApiRequest = {
            id: uuidv4(),
            chatblkUiEvent: event,
        };

        ChatBlkTelemetryService.instance.postUIEvent(uiEventApiRequest);
    }

    private static baseUiEvent() {
        const {
            aiProps: { aiEngine, appName, appVersion, sessionId },
        } = ChatBlkBaseService.instance.appContext;
        return {
            sessionId,
            chatblkId: uuidv4(),
            parentApp: appName,
            parentAppVersion: appVersion,
            beta: (appName || '').toLowerCase().includes('beta'),
            actionOrder: ++this.actionOrder,
            chatblkLaunchMode: ChatBlkTelemetryService.getLaunchMode(),
            chatblkFunction: undefined,
            eventTime: new Date().toISOString(),
            clientOverride: window.location.hostname,
            aiEngine: aiEngine.toString(),
            chatblkFeedbackEvent: undefined,
            options: {},
        };
    }

    private async postUIEvent(
        requestBody: ChatBLKUiEventApiRequest
    ): Promise<void> {
        if (ChatBlkBaseService.instance.isLoggedIn) {
            const abortController = new AbortController();

            try {
                const request = `${ChatBlkBaseService.basePath}${BLK_API_PATHS.UI_EVENT}`;

                await ChatBlkBaseService.axiosInstance.post(
                    request,
                    requestBody,
                    {
                        headers: ChatBlkBaseService.requestHeaders,
                        signal: abortController.signal,
                    }
                );
            } catch (error) {
                this.handleXhrErrors(
                    'postUIEvent',
                    error,
                    requestBody,
                    ChatBlkBaseService.requestHeaders
                );
            } finally {
                abortController?.abort();
            }
        }
    }

    private async postFeedbackEvent(
        requestBody: ChatBLKFeedbackApiRequest
    ): Promise<void> {
        const abortController = new AbortController();
        const headers = ChatBlkBaseService.requestHeaders;
        try {
            const request = `${ChatBlkBaseService.basePath}${BLK_API_PATHS.FEEDBACK}`;

            await ChatBlkBaseService.axiosInstance.post(request, requestBody, {
                headers,
                signal: abortController.signal,
            });
        } catch (error) {
            this.handleXhrErrors(
                'postFeedbackEvent',
                error,
                requestBody,
                headers
            );
        } finally {
            abortController?.abort();
        }
    }

    private static getLaunchMode(): string {
        return ChatBlkTelemetryService.getKey(
            ChatblkLaunchMode,
            ChatblkLaunchMode.CHATBLK_LAUNCH_MODE_EMBEDDED_WEB
        );
    }

    private static createFeedbackEvent({
        id,
        sessionId,
        timestampUtc,
        userTimestamp,
        responseTimestamp,
        questionCategory,
        answerRating,
        feedbackCategories,
        relatedRating,
        feedbackComment,
        feedbackRewrite,
        isThumbsUp,
    }: ChatBlkQuery): ChatBLKFeedbackEvent {
        const feedbackEvent: ChatBLKFeedbackEvent = {
            chatblkExchangeId: id,
            chatblkSessionUuid: sessionId,
            feedbackSubmitTime: new Date().toISOString(),
            responseDuration: undefined,
            actualQuestionCategory: ChatBlkTelemetryService.getKey(
                ChatblkFeedbackQuestionCategory,
                questionCategory
            ),
            chatblkAnswerQuality: ChatBlkTelemetryService.getKey(
                ChatblkFeedbackAnswerQualityRating,
                answerRating
            ),
            relatedQuestionsQuality: ChatBlkTelemetryService.getKey(
                ChatblkFeedbackRelatedQuestionsQualityRating,
                relatedRating
            ),
            additionalComment: feedbackComment,
            rewrittenResponse: feedbackRewrite,
            chatblkFeedbackNotificationType: ChatBlkTelemetryService.getKey(
                ChatblkFeedbackNotificationType,
                ChatblkFeedbackNotificationType.CHATBLK_FEEDBACK_NOTIFICATION_TYPE_CREATE
            ),
            wrongAnswerSelections: [...(feedbackCategories || [''])],
            answerHelpful: isThumbsUp,
        };
        let responseDuration: string;
        if (timestampUtc && timestampUtc > 0) {
            responseDuration = `${timestampUtc / 1000}s`;
        } else if (userTimestamp && responseTimestamp) {
            responseDuration = `${
                (new Date(responseTimestamp).getTime() -
                    new Date(userTimestamp).getTime()) /
                1000
            }s`;
        }
        feedbackEvent.responseDuration = responseDuration;

        return feedbackEvent;
    }
}
